import React, { useCallback, useEffect, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./style.scss";
import TagIcon from "../../../icons/TagIcon";
import { getPropertyList } from "../../../core/data/redux/property/_propertyAction";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "../../../icons/SearchIcon";
import { formatTime } from "../../../constants/date";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { categoryList } from "../../../services/category.service";
import LocationIcon from "../../../icons/LocationIcon";
import { getStorageItem } from "../../../services/storage.service";
import { apiPageLimit } from "../../../constants/common";

const CpaProperties: React.FC = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const propertyData = useSelector((state: any) => state?.property);
  const properties = propertyData?.propertyList;
  const totalCount = propertyData?.totalCount;

  const [categories, setCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [selected, setSelected] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;

  const owner_uuid = getStorageItem("selectedClientUuid");

  // Fetch properties on component mount
  useEffect(() => {
    dispatch(getPropertyList({ owner_uuid }));
    getCategoryList();
  }, [dispatch]);

  const getCategoryList = async () => {
    try {
      const response = await categoryList();
      const data = response?.data?.data || [];

      const categoryNameMap: Record<string, string> = {
        STR: "Short - Term Rental (STR)",
        LTR: "Long - Term Rental (LTR)",
      };

      // Convert API response into {label, value}
      const mapped = data.map((cat: any) => ({
        label:
          categoryNameMap[cat.name.toUpperCase()] || cat.name.toUpperCase(),
        value: cat.uuid,
      }));

      setCategories([{ label: "Choose Category", value: "" }, ...mapped]);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch properties with debounce
  const fetchProperties = useCallback(
    debounce(
      (search: string, category: string, page: number, owner_uuid: string) => {
        const params: any = { page, limit: pageSize };
        if (search) params.search = search;
        if (category) params.category = category;
        if (owner_uuid) params.owner_uuid = owner_uuid;
        dispatch(getPropertyList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchProperties(searchTerm, selected, page, owner_uuid);
  }, [searchTerm, selected, page, owner_uuid, fetchProperties]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Filter properties by category if selected
  const filteredProperties = selected
    ? properties?.filter((p: any) => p?.category?.uuid === selected)
    : properties;

  return (
    <>
      <div className="layout">
        {/* Sidebar */}
        <Sidebar isOpen={isSidebarOpen} />

        {/* Main Content */}
        <main className="content">
          <div className="profileSettings">
            <div className="title_btn d-flex justify-content-between gap-2 flex-wrap align-items-center">
              <div>
                <h2>Properties</h2>
              </div>
            </div>

            {/* Search + Filter */}
            <div className="block-border search_select_wrap d-flex gap-2 my-3">
              <div className="search_wrap w-100 position-relative">
                <button className="searchBtn">
                  <SearchIcon />
                </button>
                <Form.Control
                  type="text"
                  placeholder="Search  properties by name or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <Dropdown className="filterWrap">
                <Dropdown.Toggle variant="light" id="dropdown-category">
                  {selected
                    ? categories.find((c) => c.value === selected)?.label
                    : "Choose Category"}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {categories.map((opt) => (
                    <Dropdown.Item
                      key={opt?.value || "both"}
                      as="div"
                      onClick={() => setSelected(opt?.value)}
                    >
                      <Form.Check
                        type="radio"
                        name="category"
                        label={opt?.label}
                        value={opt?.value}
                        checked={selected === opt?.value}
                        readOnly
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            {/* Properties List */}
            <div className="block-border my-3">

              <div className="table-responsive">
                <table className="table table-hover propertyTable">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Address</th>
                      <th>Owner</th>
                      <th>Spouse</th>
                      <th>Total Time</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredProperties?.length > 0 ? (
                      filteredProperties.map((property: any) => {
                        const hasOwner = !!property?.track_hours?.owner;
                        const hasSpouse = !!property?.track_hours?.spouse;

                        return (
                          <tr
                            key={property.uuid}
                            onClick={() =>
                              navigate(`/cpa/properties/detail/${property?.uuid}`)
                            }
                            className="clickable-row"
                          >
                            {/* Property Name */}
                            <td>
                              <strong>{property?.name}</strong>
                            </td>

                            {/* Category */}
                            <td>
                              <div className="tag_time d-flex justify-content-between gap-3 w-100">
                                <div
                                  className={`tag_icon ${property?.category?.name?.toLowerCase() === "ltr"
                                    ? "greyColor"
                                    : ""
                                    }`}
                                >
                                  <TagIcon />
                                  {property?.category?.name?.toUpperCase()}
                                </div>
                              </div>
                            </td>

                            {/* Address */}
                            <td>
                              {property?.address}, {property?.city}, {property?.state}{" "}
                              {property?.zipcode}
                            </td>

                            {/* Owner Time */}
                            <td>
                              {hasOwner ? (
                                <div className="img_time_wrap d-flex gap-2 align-items-center">
                                  <img
                                    src={property?.user?.image ? property?.user?.image : "/assets/img/userCircle.png"}
                                    alt="owner"
                                  />
                                  {formatTime(property?.track_hours?.owner)}
                                </div>
                              ) : (
                                <div style={{ paddingLeft: '20px' }}>-</div>
                              )}
                            </td>

                            {/* Spouse Time */}
                            <td>
                              {hasSpouse ? (
                                <div className="img_time_wrap d-flex gap-2 align-items-center">
                                  <img
                                    src={property?.spouse?.image ? `${property?.spouse?.image}` : "/assets/img/userCircle.png"}
                                    alt="spouse"
                                  />
                                  {formatTime(property?.track_hours?.spouse)}
                                </div>
                              ) : (
                                <div style={{ paddingLeft: '20px' }}>-</div>
                              )}
                            </td>

                            {/* Total Time */}
                            <td>
                              {property?.track_hours?.total ? (
                                <div className="img_time_wrap d-flex gap-2 align-items-center">
                                  {formatTime(property?.track_hours?.total)}
                                </div>
                              ) : (
                                <div style={{ paddingLeft: '20px' }}>-</div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          No property found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>


              {/* Pagination */}
              <CommonPagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CpaProperties;
